import { ErrorIcon, SuccessIcon, WarningIcon } from './components/AlertIcons';
import { axiosProvider } from './axios';
import { ErrorMessageForSSARErrorComponent } from './components/ErrorMessageForSSARErrorComponent';
import { getCurrentFeatureCode, OktaUserInfo } from '@/app/core/auth';
import { unstable_batchedUpdates } from 'react-dom';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import toKebabCase from 'lodash/kebabCase';
import {
  AppUserProfile,
  convertToSSARError,
  hasPermission,
  SSARError,
  SSARErrorLike,
  buildUrl,
  NewLog,
  Feature,
  ProductNameEnum,
} from '@ssar-app/domain';
import { parse, generate } from 'lil-csv';
import { trimExtraSlashesInUri } from '@ssar-app/okta-authn-react';
import {
  AUTHZ_ANY_VALUE,
  Entitlement,
  getEntitlementForContext,
  isAuthzAnyValue,
} from '@ascend/authz';
import { Alert, Modal } from '@ssar-app/uikit';
import dayjs from 'dayjs';

export const QuestTncAcceptedStatusStorageKey = '__quest_tnc_accepted__';
export const UsaaTncAcceptedStatusStorageKey = '__usaa_tnc_accepted__';

export const featureMountPathBuilder: (feature: Feature) => string = (
  feature,
) => {
  const kebabedCode = toKebabCase(feature.code);
  return trimExtraSlashesInUri(`/${kebabedCode.replace('ascend-', '')}`);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ModalFunc = (...args: any[]) => any;

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const NoOp = () => {};

const showSuccessMessage = (
  message?: React.ReactNode,
  onOk?: ModalFunc,
  onCancel?: ModalFunc,
): void => {
  Modal.success({
    onOk,
    onCancel,
    icon: <SuccessIcon />,
    header: 'Success',
    content: message,
    headerProps: { fontSize: '3xl', textAlign: 'center', display: 'block' },
    contentProps: { textAlign: 'center' },
    scrollBehavior: 'outside',
    okButtonProps: { size: 'sm' },
  });
};

const showWarningMessage = (
  title: string,
  message?: React.ReactNode,
  onOk?: ModalFunc,
  onCancel?: ModalFunc,
): void => {
  Modal.info({
    onOk,
    onCancel,
    icon: <WarningIcon />,
    header: title,
    content: message,
    headerProps: { fontSize: '2xl', textAlign: 'center', display: 'block' },
    contentProps: { textAlign: 'center' },
    scrollBehavior: 'outside',
    okButtonProps: { size: 'sm' },
  });
};

const buildErrorMessageNodeFromSSARError = (
  error: SSARErrorLike,
): React.ReactNode => {
  return <ErrorMessageForSSARErrorComponent error={error} />;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildErrorMessageNode = (error: any): React.ReactNode => {
  // TODO:(CV) Apart from SSARError, Error, AxiosError(should this be transformed at the response handling level.) check for APiResponse
  if (SSARError.isSSARError(error)) {
    return buildErrorMessageNodeFromSSARError(error);
  } else if (typeof error === 'string') {
    return (
      <Alert
        description={error.toString()}
        type='error'
        isOpen
        showCloseButton={false}
        showIcon={false}
      ></Alert>
    );
  }
  return buildErrorMessageNodeFromSSARError(convertToSSARError(error));
};

const showError = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  title?: string | undefined | null,
  onOk?: ModalFunc,
  onCancel?: ModalFunc,
): void => {
  // console.error(error);
  const errorMessageNode = buildErrorMessageNode(error);
  const reloadForErrors = ['InvalidCSRFTokenError'];
  Modal.error({
    icon: <ErrorIcon />,
    onOk,
    onCancel,
    ...(reloadForErrors.includes(error?.name)
      ? {
          showCancel: true,
          okText: 'Reload',
          onOk: () => {
            window.location.reload();
          },
        }
      : {}),
    header: title || 'Error',
    headerProps: {
      fontSize: 'xl',
      mt: 6,
      textAlign: 'center',
      display: 'block',
    },
    content: errorMessageNode,
    scrollBehavior: 'outside',
    okButtonProps: { size: 'sm' },
    cancelButtonProps: { size: 'sm' },
  });
};

export const stringUtils = {
  formatWithSperator(
    input?: number | string,
    seperator = ',',
  ): string | undefined {
    return input?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, seperator);
  },
  truncate(input: string, maxChars = 50): string | undefined {
    return input?.length > maxChars
      ? `${input.substring(0, maxChars)}...`
      : input;
  },
  camelToSnakeCase(input: string): string | undefined {
    return input.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  },
  camelToPascalCase(input: string): string | undefined {
    return input.replace(
      /\w\S*/g,
      (m) => m.charAt(0).toUpperCase() + m.substring(1).toLowerCase(),
    );
  },
  titleCase(input: string): string | undefined {
    return input
      .toLowerCase()
      .split(' ')
      .map((word) => {
        return word.replace(word[0], word[0].toUpperCase());
      })
      .join(' ');
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isNumeric(n: any) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  },
};
export const getUserProfile = (): Promise<AppUserProfile> => {
  return new Promise((resolve, reject) => {
    axiosProvider.apiClient
      .get<AppUserProfile>(`/authz/profile`)
      .then((response) => {
        resolve(response?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getUserPermissions = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    axiosProvider.apiClient
      .get<Record<string, string[]>>(`/user/permission`)
      .then((response) => {
        localStorage.setItem(
          'ssar-user-permissiosn',
          JSON.stringify(response?.data),
        );
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const saveLog = (event: string, content: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const logData: NewLog = {
      event: event,
      content: content,
      date: new Date(),
    };
    axiosProvider.apiClient
      .post<string>(`/log`, logData)
      .then((response) => {
        resolve(response?.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const hasPermissionToRender = (
  featureCode: string | undefined | null,
  permission: string,
): boolean => {
  if (!featureCode) {
    return false;
  }

  const userProfile = OktaUserInfo.getOktaUserInfo();
  return hasPermission(
    featureCode,
    permission,
    userProfile,
    isAuthzAnyValue(userProfile.entitlements)
      ? AUTHZ_ANY_VALUE
      : getEntitlementForContext(
          getCurrentFeatureCode() || '',
          userProfile.entitlements as Entitlement[],
        )?.party || null,
  );
};

export const generateId = (): string => {
  const d = Date.now() * 1000;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16 + d) % 16 | 0;

    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// const isEqual = isEqual;
export const modalUtils = {
  showSuccessMessage,
  showWarningMessage,
  showError,
};

export const objectUtils = {
  areEqual: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    other: any,
    ...ignoreProps: Array<string>
  ): boolean => {
    return isEqual(omit(value, ignoreProps), omit(other, ignoreProps));
  },
  isEmpty,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  areAllPropsEmpty: (object: Record<string, any>): boolean =>
    Object.values(object).every((x) => x === null || x.toString() === ''),
};

function replaceIndex<T>(source: T[], index: number, value: T): T[] {
  return Object.assign([], source, { [index]: value });
}

export const arrayUtils = {
  replaceIndex,
  createRange(limit: number): number[] {
    return Array.from(Array(limit).keys());
  },
};

export const toAppUrl = (segment: string) => {
  return buildUrl(import.meta.env.BASE_URL || '/ssar')(segment);
};

export const packKeys =
  <Key extends string>(delimiter = '|') =>
  (args: Key[]): string => {
    const generatedKey = generate([args], { header: false, delimiter });
    return generatedKey;
  };

export const unpackKeys =
  (delimiter = '|') =>
  (arg: string): string[] => {
    const parsedKeys = parse(arg, { header: false, delimiter })[0];
    return parsedKeys;
  };

// TODO: (CV) This needs to be revisited after upgrading to React 18: https://github.com/reactwg/react-18/discussions/21
export const batchReactStateUpdates = (callback: () => void) => {
  unstable_batchedUpdates(callback);
};

export const toSafeArray = <TType,>(input: TType | TType[]): TType[] => {
  if (!input) {
    return [];
  } else if (Array.isArray(input)) {
    return input;
  }
  return [input];
};

export const canAccessQuestProduct = (date?: string) => {
  if (getCurrentFeatureCode() !== ProductNameEnum.AscendQuest || !date) {
    return true;
  }
  const givenDate = dayjs(date, 'YYYY-MM-DD').toDate();
  const currDate = dayjs().toDate();
  return currDate >= givenDate ? false : true;
};
